import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles, Heart, Calendar, Users, DollarSign, ArrowRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const StartHereGuide = ({ user, weddingPlan, tasks, guests, chatHistory, onOpenVAL, onOpenGuests, onOpenBudget, onOpenLocation, onRefresh }) => {
  const navigate = useNavigate();
  const [skippedSteps, setSkippedSteps] = React.useState(() => {
    // Load skipped steps from localStorage
    const saved = localStorage.getItem(`onboarding_skipped_${user.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  // Save skipped steps to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem(`onboarding_skipped_${user.id}`, JSON.stringify(skippedSteps));
  }, [skippedSteps, user.id]);

  const markAsSkipped = (stepId) => {
    if (!skippedSteps.includes(stepId)) {
      setSkippedSteps([...skippedSteps, stepId]);
      onRefresh(); // Refresh to update UI
    }
  };

  const gettingStartedTasks = tasks.filter(t => t.category === 'getting_started');
  const completedGettingStarted = gettingStartedTasks.filter(t => t.completed);
  const progressPercent = gettingStartedTasks.length > 0 
    ? (completedGettingStarted.length / gettingStartedTasks.length) * 100 
    : 0;

  const steps = [
    {
      id: 'set_location',
      title: 'Set Wedding Location',
      description: 'Tell us where you\'re planning to celebrate (within 50 miles)',
      completed: !!weddingPlan?.venue_location || skippedSteps.includes('set_location'),
      action: onOpenLocation,
      icon: MapPin,
      color: 'text-[#881337]'
    },
    {
      id: 'set_date',
      title: 'Set Your Wedding Date',
      description: 'Choose your special day to unlock your personalized timeline',
      completed: !!weddingPlan?.wedding_date || skippedSteps.includes('set_date'),
      action: () => {
        // Scroll to and trigger the wedding date button
        setTimeout(() => {
          const button = document.querySelector('[data-testid="set-wedding-date-btn"]');
          if (button) {
            button.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => button.click(), 500);
          }
        }, 100);
      },
      icon: Calendar,
      color: 'text-[#881337]'
    },
    {
      id: 'chat_val',
      title: 'Meet Grace',
      description: 'Chat with your AI wedding planner about your vision',
      completed: chatHistory.length > 0 || skippedSteps.includes('chat_val'),
      action: onOpenVAL,
      icon: Sparkles,
      color: 'text-[#881337]'
    },
    {
      id: 'add_guests',
      title: 'Start Guest List',
      description: 'Add your first guests to get an accurate headcount',
      completed: (guests && guests.length > 0) || skippedSteps.includes('add_guests'),
      action: onOpenGuests,
      icon: Users,
      color: 'text-[#881337]'
    },
    {
      id: 'set_budget',
      title: 'Set Budget',
      description: 'Define your total wedding budget to track expenses',
      completed: (weddingPlan?.total_budget && weddingPlan.total_budget > 0) || skippedSteps.includes('set_budget'),
      action: onOpenBudget,
      icon: DollarSign,
      color: 'text-[#881337]'
    }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const allCompleted = completedCount === steps.length;

  // Hide the guide when all steps are completed
  if (allCompleted) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-3xl p-8 mb-8 text-white shadow-2xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-3xl font-serif font-light mb-2">Start Here, {user.partner1_name}!</h2>
          <p className="text-rose-100">Complete these steps to get your planning off to a perfect start</p>
        </div>
        <div className="text-right">
          <div className="text-4xl font-serif">{completedCount}/{steps.length}</div>
          <div className="text-sm text-rose-100">Steps Done</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white/20 rounded-full h-2 mb-6 overflow-hidden">
        <div 
          className="bg-white h-full rounded-full transition-all duration-500"
          style={{ width: `${(completedCount / steps.length) * 100}%` }}
        />
      </div>

      {/* Steps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <button
              key={step.id}
              data-testid={`start-here-${step.id}`}
              onClick={step.action}
              disabled={step.completed}
              className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-left transition-all duration-300 border-2 ${
                step.completed 
                  ? 'border-white/50 opacity-60' 
                  : 'border-transparent hover:border-white/50 hover:bg-white/20 transform hover:-translate-y-1'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  {step.completed ? (
                    <CheckCircle size={24} className="text-[#84A98C]" fill="#84A98C" />
                  ) : (
                    <Icon size={24} />
                  )}
                </div>
                {!step.completed && (
                  <ArrowRight size={20} className="text-white/60" />
                )}
              </div>
              <h3 className="font-medium mb-1">{step.title}</h3>
              <p className="text-sm text-rose-100 leading-relaxed">{step.description}</p>
              {step.completed && (
                <div className="mt-3 text-xs text-[#84A98C] font-medium">✓ Completed</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Getting Started Tasks Progress */}
      {gettingStartedTasks.length > 0 && (
        <div className="mt-6 pt-6 border-t border-white/20">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-medium">Your Getting Started Tasks</h3>
              <p className="text-sm text-rose-100">{completedGettingStarted.length} of {gettingStartedTasks.length} completed</p>
            </div>
            <Button
              onClick={() => navigate('/timeline')}
              variant="ghost"
              className="text-white hover:bg-white/20 rounded-full"
            >
              View All Tasks <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
          <div className="bg-white/20 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-white h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StartHereGuide;